import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  ViewChild,
  Renderer2,
} from '@angular/core';

import { AutocompleteSelectedItem, AutocompleteSettings, AutocompleteUiItem } from './search-input.model';
import { StateService } from '../../services/state.service';
import { StateRxService } from '../../services/staterx.service';
import { UiService } from '../../services/ui.service';

@Component({
  selector: 'hcf-autocomplete',
  templateUrl: './autocomplete.component.html',
  standalone: false,
})
export class AutocompleteComponent implements OnInit, OnChanges {
  @ViewChild('autocompleteInput') autocompleteInput: ElementRef;

  @Input() componentId: string;
  @Input() tabIndex: number;
  @Input() autoCompleteData: AutocompleteUiItem[] = [];
  @Input() autocompleteSettings: AutocompleteSettings;
  @Output() onInput = new EventEmitter<string>();
  @Output() onFocus = new EventEmitter<boolean>();
  @Output() onGetData = new EventEmitter();
  @Output() onAutocompleteItemSelected = new EventEmitter<AutocompleteSelectedItem>();
  @Output() onEnter = new EventEmitter<string>();

  // A reference to the setTimeout timer, so we only have one timer in use and it can be cleared when needed.
  private _getDataTimeout: any;
  private _inputDelay: number; // Delay after last user input before firing the search service, miliseconds. Set in parent settings.
  private _inputMinChar: number; // Minimum number of characters before firing the search. Set in parent.
  private _inputInFocus: boolean = false;
  private _selectedAutocompleteItem: number = -1;
  // Had some issues with cancelling previous data requests using observables, use this flag instead for now to ignore incoming data
  // if it's not needed anymore.
  private _expectingData: boolean = false;
  // Local variable to assign the @Input autoCompleteData.
  localAutocompleteData: AutocompleteUiItem[] = [];

  inputText: string = '';
  inputPlaceholder: string = '';
  hideAutoComplete: boolean = true;
  hideProgress: boolean = true; // hide/show the autocomplete loading indicator

  counter: number;

  constructor(
    private _stateService: StateService,
    _stateRx: StateRxService,
    private _uiService: UiService,
    private renderer: Renderer2,
  ) {
    // TODO: add unsubscribe on destroy. In the current version this component is loaded once and is never unloaded,
    // so currently it's ok not to unsubscribe.
    _stateRx.escPressed.rxSubscribe(() => {
      this._expectingData = false;
      this.hideAutoComplete = true;
      this.hideProgress = true;
      clearTimeout(this._getDataTimeout);
    });

    // Subscribe to arrow down key press
    _stateRx.arrowDownPressed.rxSubscribe(() => {
      if (this._inputInFocus && !this.hideAutoComplete) {
        // If press down on the last item then jump to the top, nothing will be selected and we can keep going down the list.
        if (this._selectedAutocompleteItem === this.autoCompleteData.length - 1) {
          this._selectedAutocompleteItem = -1;
          this.autoCompleteData.forEach((d) => (d.selected = false));
          return;
        }

        if (this._selectedAutocompleteItem < this.autoCompleteData.length - 1) {
          this._selectedAutocompleteItem++;
          let selectedItem = this.autoCompleteData[this._selectedAutocompleteItem];
          // Skip unclicable item but only if it's not the last item like for example the NoResults item
          if (!selectedItem.isClickable && this._selectedAutocompleteItem < this.autoCompleteData.length - 2) {
            this._selectedAutocompleteItem++;
            selectedItem = this.autoCompleteData[this._selectedAutocompleteItem];
          }
          this.autoCompleteData.forEach((d) => (d.selected = false));
          selectedItem.selected = true;
        }
      }
    });

    // Subscribe to arrow up key press
    _stateRx.arrowUpPressed.rxSubscribe(() => {
      if (this._inputInFocus && !this.hideAutoComplete) {
        // If press up while nothing is selected then start from the bottom of the list
        if (this._selectedAutocompleteItem === -1 && this.autoCompleteData.length > 1) {
          this._selectedAutocompleteItem = this.autoCompleteData.length;
        }

        // Remove highlight from the first item if arrow up is pressed
        if (this._selectedAutocompleteItem === 0) {
          this._selectedAutocompleteItem--;
          this.autoCompleteData.forEach((d) => (d.selected = false));
        }

        if (this._selectedAutocompleteItem > 0) {
          this._selectedAutocompleteItem--;
          let selectedItem = this.autoCompleteData[this._selectedAutocompleteItem];
          // Skip unclickable items
          if (!selectedItem.isClickable && this._selectedAutocompleteItem > -1) {
            this._selectedAutocompleteItem--;
            selectedItem = this.autoCompleteData[this._selectedAutocompleteItem];
          }
          this.autoCompleteData.forEach((d) => (d.selected = false));
          selectedItem.selected = true;
        }
      }
    });

    // Subscribe to Enter press
    _stateRx.enterPressed.rxSubscribe(() => {
      // Clear timeout if enter is pressed, if enter takes user to the search result page then
      // we don't want the autocomplete dropdown to popup due to delay
      clearTimeout(this._getDataTimeout);
      this.hideProgress = true;
      this._expectingData = false;

      // If nothing is selected in a dropdown but enter is pressed then pass null data to parent component
      if (this._inputInFocus && this._selectedAutocompleteItem < 0) {
        this.onAutocompleteItemClick(null);
      } else if (this._inputInFocus && !this.hideAutoComplete && this._selectedAutocompleteItem >= 0) {
        // If something was selected from the dropdown.
        this.onAutocompleteItemClick(this.autoCompleteData[this._selectedAutocompleteItem]);
      } else {
        this.hideAutoComplete = true;
      }
    });
  }

  ngOnInit() {
    // Load settings passed from the parent.
    this._inputDelay = this.autocompleteSettings.inputDelay;
    this._inputMinChar = this.autocompleteSettings.inputMinChar;
    this.inputPlaceholder = this.autocompleteSettings.inputPlaceholder;
    this.initAutocomplete();
  }

  // Fired when Input data changes
  ngOnChanges() {
    // Home page has a different layout for input boxes.
    // Reset the autocomplete position when getting data so we get the right autocomplete layout no matter what page we're on.
    this.autocompleteResetPosition();

    this.hideProgress = true;
    if (this.autoCompleteData && this._expectingData) {
      this._selectedAutocompleteItem = -1;
      this.localAutocompleteData = this.autoCompleteData;
      if (this.autoCompleteData.length === 1) {
        this.hideAutoComplete = !this._inputInFocus;
      } else {
        this.hideAutoComplete =
          !this.inputText || this.inputText.length < this._inputMinChar || this.autoCompleteData.length === 0;
      }
    } else {
      this.localAutocompleteData = [];
    }
  }

  onTextInput(event: string) {
    if (event) {
      event = event.trim();
    }

    this.inputText = event;

    if (!event || event.length < this._inputMinChar) {
      this.hideAutoComplete = true;

      // emit empty string so parent is updated that nothing is selected
      this.onInput.emit(null);

      // Clear the timeout in case if the user is quickly pressing the Backspace, so the getDataWithDelay below
      // doesn't fire after the keyword is less than the _inputMinChar.
      clearTimeout(this._getDataTimeout);
      return; // Don't seacrh if keywords are empty? Need to take location into account as well.
    } else {
      this.onInput.emit(event);
      this.getDataWithDelay();
    }
  }

  private getDataWithDelay() {
    this._expectingData = true;

    // _getDataTimeout will be wired on each keystroke, clear it before setting a new one
    // setTimeout will emit onGetData event once the input is stopped for a specified time interval
    clearTimeout(this._getDataTimeout);

    this._getDataTimeout = setTimeout(() => {
      this.hideProgress = false;
      this.onGetData.emit();
    }, this._inputDelay);
  }

  /** Event handler when items selected from the autocomplete dropdown
   * @param event .
   * @param item - Data item
   */
  onAutocompleteItemClick(item: AutocompleteUiItem) {
    this._selectedAutocompleteItem = -1;
    if (item && item.cssClass !== 'my-location') {
      this._stateService.clearLastTopSearchResponse();
    }

    // If nothing was selected and the Enter key pressed then item will be null.
    // In this case parent will redirect to the search results.
    let searchTerm: string = this.inputText;
    let selectedItem: AutocompleteSelectedItem = {
      autocompleteUiItem: null,
      searchInput: this.inputText,
      searchTerm,
    };

    if (item != null) {
      if (item.populateInputWithName) {
        this.inputText = item.data.name;
        searchTerm = item.data.name;
      }
      if (item.populateSearchTermWithName) {
        searchTerm = item.data.name;
      }
      selectedItem = {
        autocompleteUiItem: item,
        searchInput: this.inputText,
        searchTerm,
      };
    }
    const selectedType = item && item.data ? item.data.type.toLowerCase() : undefined;
    if (selectedType !== 'location' && selectedType !== undefined) {
      this._stateService.saveLastSelected(selectedItem);
    }
    this.onAutocompleteItemSelected.emit(selectedItem);
    this.hideAutoComplete = true;
  }

  onAutocompleteItemHover(item: AutocompleteUiItem) {
    this._selectedAutocompleteItem = item.index;
    this.autoCompleteData.forEach((x) => (x.selected = false));
    item.selected = true;
  }

  onInputFocus() {
    this._inputInFocus = true;
    this._expectingData = true; // Used by My location, we need to display it on focus.

    this.onFocus.emit(true);

    // Show the dropdown on focus if there is something in the input box
    if (!this.inputText || this.inputText.length < this._inputMinChar) {
      return;
    }

    this.getDataWithDelay();
  }

  onInputBlur() {
    // Use 'mousedown' event for autocomplete items onAutocompleteItemClick. 'mousedown' is fired before blur.
    // If 'click' event is used then the blur would fire first and then click which means if we hide the autocomplete on blur
    // the click won't register because the dropdown will be closed.

    this._inputInFocus = false;
    this._expectingData = false;
    this.hideProgress = true;
    this.onFocus.emit(false);

    // If input is not empty then dropdown will appear on focus using the same delay as keyboard input.
    // Clear the timeout on blur, so if user switching boxes back and forward we cancel the previously fired onfocus search request.
    clearTimeout(this._getDataTimeout);
    this.hideAutoComplete = true;
  }

  /**
   * Bind autocomplete reposition on window resize.
   */
  initAutocomplete() {
    this.autocompleteResetPosition();
    this._uiService.bindWindowResize(() => {
      this.autocompleteResetPosition();
    }); // reposition autocomplete on window resize

    // Bind keypress event to current component's input.
    this._uiService.bindKeyDown('#' + this.componentId + ' input', (event) => {
      this._stateService.keyPressed(event.which);
    });
  }

  /** Helper method to position the autocomplete dropdown for the responsive layout.
   * Call this on window.resize to reposition and change the width to match the input width.
   */
  private autocompleteResetPosition() {
    this._uiService.autocompleteResetDropdownWidth(this.componentId);
  }

  giveInputFocus() {
    setTimeout(() => {
      this.autocompleteInput.nativeElement.focus();
    });
  }

  resetInputValue(value: string) {
    this.inputText = value;
    this.onInput.emit(null);
  }
}
